import React, {useCallback, useEffect, useRef, useState} from 'react'
import {Form, Button, Row, Col, Spinner} from 'react-bootstrap'
import {useAuth} from '../../auth'
import {OP} from '../core/operationPermissions'
import {useOperationsAccess} from '../core/useOperationsAccess'
import {
  getActiveVoyageForVessel,
  getConsumptionByVoyageReport,
  getConsumptionFuelTypes,
  getConsumptionMachineryTypes,
  getConsumptionByVoyage,
  submitConsumptionROB,
} from '../core/_requests'
import type {Voyage} from '../core/_models'
import {
  buildConsumptionRequestDto,
  defaultEmptyRobRow,
  mapConsumptionResponseToRows,
  type RobOuterRow,
} from '../core/consumptionRobUtils'

export type ConsumptionAndROBProps = {
  /** Vessel chosen in Library filters; required to load voyages and consumption. */
  vesselId?: number
  /** Optional submitted report id (position report instance) to align with voyage-scoped consumption. */
  reportInstanceId?: number
}

const PLACEHOLDER_FUEL = [
  {label: 'Select Fuel Type', value: ''},
  {label: 'HFO', value: 'HFO'},
  {label: 'VLSFO', value: 'VLSFO'},
  {label: 'LSFO', value: 'LSFO'},
  {label: 'MGO', value: 'MGO'},
  {label: 'MDO', value: 'MDO'},
  {label: 'ULSFO', value: 'ULSFO'},
  {label: 'LNG', value: 'LNG'},
  {label: 'Methanol', value: 'Methanol'},
  {label: 'BioFuel', value: 'BioFuel'},
  {label: 'LPG', value: 'LPG'},
  {label: 'Hydrogen / Ammonia', value: 'Hydrogen / Ammonia'},
]

const PLACEHOLDER_MACHINERY = [
  {label: 'Select machinery', value: ''},
  {label: 'ME', value: 'ME'},
  {label: 'ME 1', value: 'ME 1'},
  {label: 'ME 2', value: 'ME 2'},
  {label: 'AE', value: 'AE'},
  {label: 'AE 1', value: 'AE 1'},
  {label: 'AE 2', value: 'AE 2'},
  {label: 'AE 3', value: 'AE 3'},
  {label: 'AE 4', value: 'AE 4'},
  {label: 'Boiler', value: 'Boiler'},
  {label: 'Boiler-1', value: 'Boiler-1'},
  {label: 'Boiler-2', value: 'Boiler-2'},
  {label: 'Misc', value: 'Misc'},
]

const DEFAULT_MACHINERY_OPTIONS = PLACEHOLDER_MACHINERY
  .map((option) => option.label)
  .filter((label) => label !== 'Select machinery')

const ConsumptionAndROB: React.FC<ConsumptionAndROBProps> = ({vesselId, reportInstanceId}) => {
  const authCtx = useAuth() as any
  const {currentUser} = authCtx
  const {opCan, permissionsLoaded} = useOperationsAccess()
  const canView = opCan(OP.READ_IN_PORT_CONSUMPTION_REPORT)
  const canSave = opCan(OP.UPDATE_IN_PORT_CONSUMPTION_REPORT)

  const token =
    authCtx?.auth?.auth?.jwt ||
    authCtx?.auth?.jwt ||
    sessionStorage.getItem('jwt') ||
    sessionStorage.getItem('token') ||
    ''

  const userId = Number(currentUser?.id ?? 0)
  const companyId = Number(
    currentUser?.company?.id ??
      (currentUser as any)?.companyId ??
      (currentUser as any)?.companyAdminId ??
      (currentUser as any)?.companyGroupAdminId ??
      0
  )

  const [fuelOptions, setFuelOptions] = useState<string[]>([])
  const [machineryOptions, setMachineryOptions] = useState<string[]>([])
  const [optionsLoading, setOptionsLoading] = useState(true)

  const [voyages, setVoyages] = useState<Voyage[]>([])
  const [voyageId, setVoyageId] = useState<number | ''>('')
  const [voyagesLoading, setVoyagesLoading] = useState(false)
  const [voyagesError, setVoyagesError] = useState<string | null>(null)

  const [rows, setRows] = useState<RobOuterRow[]>([defaultEmptyRobRow()])
  const [consumptionLoading, setConsumptionLoading] = useState(false)
  const [consumptionError, setConsumptionError] = useState<string | null>(null)

  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveOk, setSaveOk] = useState(false)
  const [saving, setSaving] = useState(false)
  const savingRef = useRef(false)

  useEffect(() => {
    let active = true
    const run = async () => {
      setOptionsLoading(true)
      try {
        const [fuels, machs] = await Promise.all([
          getConsumptionFuelTypes(token || undefined),
          getConsumptionMachineryTypes(token || undefined),
        ])
        if (!active) return
        setFuelOptions(Array.isArray(fuels) && fuels.length ? fuels : PLACEHOLDER_FUEL.map((x) => x.label).slice(1))
        setMachineryOptions(Array.from(new Set([
          ...DEFAULT_MACHINERY_OPTIONS,
          ...(Array.isArray(machs) ? machs : []),
        ])))
      } catch {
        if (!active) return
        setFuelOptions(PLACEHOLDER_FUEL.map((x) => x.label).slice(1))
        setMachineryOptions(DEFAULT_MACHINERY_OPTIONS)
      } finally {
        if (active) setOptionsLoading(false)
      }
    }
    run()
    return () => {
      active = false
    }
  }, [token])

  useEffect(() => {
    if (!vesselId || vesselId <= 0) {
      setVoyages([])
      setVoyageId('')
      setVoyagesError(null)
      return
    }
    let active = true
    setVoyagesLoading(true)
    setVoyagesError(null)
    getActiveVoyageForVessel(vesselId)
      .then((list) => {
        if (!active) return
        const arr = Array.isArray(list) ? list : []
        setVoyages(arr)
        if (arr.length === 1) {
          setVoyageId(Number(arr[0].id))
        } else {
          setVoyageId('')
        }
      })
      .catch((e: Error) => {
        if (!active) return
        setVoyagesError(e?.message || 'Could not load voyages for this vessel.')
        setVoyages([])
      })
      .finally(() => {
        if (active) setVoyagesLoading(false)
      })
    return () => {
      active = false
    }
  }, [vesselId])

  const loadConsumption = useCallback(async () => {
    const vid = typeof voyageId === 'number' ? voyageId : 0
    if (!vid) {
      setRows([defaultEmptyRobRow()])
      return
    }
    setConsumptionLoading(true)
    setConsumptionError(null)
    setSaveOk(false)
    try {
      const rid = reportInstanceId != null && reportInstanceId > 0 ? reportInstanceId : 0
      let data: any = null
      if (rid > 0) {
        try {
          data = await getConsumptionByVoyageReport(vid, rid)
        } catch {
          data = null
        }
      }
      if (!data?.rows?.length) {
        data = await getConsumptionByVoyage(token, vid)
      }
      setRows(mapConsumptionResponseToRows(data))
    } catch (e: any) {
      setConsumptionError(e?.message || 'Failed to load consumption.')
      setRows([defaultEmptyRobRow()])
    } finally {
      setConsumptionLoading(false)
    }
  }, [voyageId, reportInstanceId])

  useEffect(() => {
    if (typeof voyageId === 'number' && voyageId > 0) {
      void loadConsumption()
    } else {
      setRows([defaultEmptyRobRow()])
      setConsumptionError(null)
    }
  }, [voyageId, loadConsumption])

  const handleOuterChange = (index: number, field: 'fieldType' | 'initialRob' | 'lastRob', value: string) => {
    if (!canSave) return
    setRows((prev) => {
      const next = [...prev]
      next[index] = {...next[index], [field]: value}
      return next
    })
  }

  const handleInnerChange = (
    outerIndex: number,
    innerIndex: number,
    field: keyof RobOuterRow['machineries'][0],
    value: string
  ) => {
    if (!canSave) return
    setRows((prev) => {
      const next = [...prev]
      const m = [...next[outerIndex].machineries]
      m[innerIndex] = {...m[innerIndex], [field]: value}
      next[outerIndex] = {...next[outerIndex], machineries: m}
      return next
    })
  }

  const addOuterRow = () => {
    if (!canSave) return
    setRows((prev) => [...prev, defaultEmptyRobRow()])
  }

  const addInnerRow = (outerIndex: number) => {
    if (!canSave) return
    setRows((prev) => {
      const next = [...prev]
      next[outerIndex] = {
        ...next[outerIndex],
        machineries: [...next[outerIndex].machineries, {machinery: '', consumed: '', rob: ''}],
      }
      return next
    })
  }

  const removeOuterRow = (index: number) => {
    if (!canSave) return
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaveError(null)
    setSaveOk(false)
    if (savingRef.current) return
    if (!canSave) return
    const vid = typeof voyageId === 'number' ? voyageId : 0
    if (!vid) {
      setSaveError('Select a voyage before saving.')
      return
    }
    const dto = buildConsumptionRequestDto(
      rows,
      vid,
      userId,
      companyId,
      reportInstanceId != null && reportInstanceId > 0 ? reportInstanceId : undefined
    )
    if (!dto.rows?.length) {
      setSaveError('Add at least one fuel row with a fuel type before saving.')
      return
    }
    savingRef.current = true
    setSaving(true)
    try {
      await submitConsumptionROB(token, dto)
      setSaveOk(true)
      await loadConsumption()
    } catch (err: any) {
      setSaveError(err?.message || 'Save failed.')
    } finally {
      savingRef.current = false
      setSaving(false)
    }
  }

  if (permissionsLoaded && !canView) {
    return <div className="alert alert-warning m-0">You do not have permission to perform this action.</div>
  }

  const fuelSelectOptions = [{label: 'Select Fuel Type', value: ''}, ...fuelOptions.map((f) => ({label: f, value: f}))]
  const machSelectOptions = [{label: 'Select machinery', value: ''}, ...machineryOptions.map((f) => ({label: f, value: f}))]

  return (
    <Form onSubmit={handleSubmit}>
      {!vesselId || vesselId <= 0 ? (
        <p className="text-muted small mb-3">Select a vessel in the library filters to load voyages and consumption data.</p>
      ) : (
        <p className="text-muted small mb-3">
          Consumption is stored per voyage (server-enforced scope). Choose a voyage, edit values, then save. Requires
          update permission on reports.
        </p>
      )}

      {voyagesError && <div className="alert alert-warning py-2 mb-2">{voyagesError}</div>}
      {consumptionError && <div className="alert alert-warning py-2 mb-2">{consumptionError}</div>}
      {saveError && <div className="alert alert-danger py-2 mb-2">{saveError}</div>}
      {saveOk && <div className="alert alert-success py-2 mb-2">Saved successfully.</div>}

      {vesselId && vesselId > 0 && (
        <div className="mb-3">
          <Form.Label className="small text-muted">Voyage</Form.Label>
          {voyagesLoading ? (
            <Spinner animation="border" size="sm" className="ms-2" />
          ) : (
            <Form.Select
              className="form-select form-select-sm"
              style={{maxWidth: 420}}
              value={voyageId === '' ? '' : String(voyageId)}
              onChange={(e) => {
                const v = e.target.value ? Number(e.target.value) : ''
                setVoyageId(v === 0 || Number.isNaN(v) ? '' : v)
              }}
            >
              <option value="">{voyages.length ? '-- Select voyage --' : 'No voyages'}</option>
              {voyages.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.voyageNumber || v.id}
                  {v.status ? ` — ${v.status}` : ''}
                </option>
              ))}
            </Form.Select>
          )}
        </div>
      )}

      {(optionsLoading || consumptionLoading) && (
        <div className="text-muted small mb-2">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading…
        </div>
      )}

      {rows.map((row, outerIndex) => (
        <div key={outerIndex} className="mb-4 border p-3 rounded">
          <Row className="mb-2 align-items-end">
            <Col md={4}>
              <Form.Group>
                <Form.Label style={{color: '#000', fontWeight: '500', fontSize: 15, marginLeft: '5px'}}>Field Type</Form.Label>
                <Form.Select
                  value={row.fieldType}
                  disabled={!canSave}
                  onChange={(e) => handleOuterChange(outerIndex, 'fieldType', e.target.value)}
                >
                  {fuelSelectOptions.map((o, i) => (
                    <option key={i} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label style={{color: '#000', fontWeight: '500', fontSize: 15, marginLeft: '5px'}}>Initial ROB</Form.Label>
                <Form.Control
                  type="number"
                  inputMode="decimal"
                  step="any"
                  value={row.initialRob}
                  disabled={!canSave}
                  onChange={(e) => handleOuterChange(outerIndex, 'initialRob', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label style={{color: '#000', fontWeight: '500', fontSize: 15, marginLeft: '5px'}}>Last ROB</Form.Label>
                <Form.Control
                  type="number"
                  inputMode="decimal"
                  step="any"
                  value={row.lastRob}
                  disabled={!canSave}
                  onChange={(e) => handleOuterChange(outerIndex, 'lastRob', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2} className="d-flex align-items-end justify-content-end gap-1">
              <Button variant="primary" size="sm" disabled={!canSave} onClick={addOuterRow} type="button">
                + Row
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                disabled={!canSave || rows.length <= 1}
                onClick={() => removeOuterRow(outerIndex)}
                type="button"
              >
                ×
              </Button>
            </Col>
          </Row>

          {row.machineries.map((machineryRow, innerIndex) => (
            <Row key={innerIndex} className="mb-2 mx-0 mx-md-4">
              <Col md={4}>
                <Form.Group>
                  <Form.Label style={{color: '#000', fontWeight: '500', fontSize: 15, marginLeft: '5px'}}>Machinery</Form.Label>
                  <Form.Select
                    value={machineryRow.machinery}
                    disabled={!canSave}
                    onChange={(e) => handleInnerChange(outerIndex, innerIndex, 'machinery', e.target.value)}
                  >
                    {machSelectOptions.map((o, i) => (
                      <option key={i} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label style={{color: '#000', fontWeight: '500', fontSize: 15, marginLeft: '5px'}}>Consumed</Form.Label>
                  <Form.Control
                    type="number"
                    value={machineryRow.consumed}
                    disabled={!canSave}
                    onChange={(e) => handleInnerChange(outerIndex, innerIndex, 'consumed', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label style={{color: '#000', fontWeight: '500', fontSize: 15, marginLeft: '5px'}}>ROB</Form.Label>
                  <Form.Control
                    type="number"
                    value={machineryRow.rob}
                    disabled={!canSave}
                    onChange={(e) => handleInnerChange(outerIndex, innerIndex, 'rob', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={2} className="d-flex align-items-end">
                <Button variant="success" size="sm" disabled={!canSave} onClick={() => addInnerRow(outerIndex)} type="button">
                  + Mach
                </Button>
              </Col>
            </Row>
          ))}
        </div>
      ))}

      <div className="text-center">
        <Button type="submit" variant="primary" disabled={!canSave || saving || !voyageId}>
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving…
            </>
          ) : (
            'Save'
          )}
        </Button>
      </div>
    </Form>
  )
}

export default ConsumptionAndROB
